// frontend/src/pages/storeActivity/FeatureAreasPanel.jsx
//
// Which parts of the app this store's people use — one row per feature
// area, in a fixed order so two stores compare line for line. Every area
// is on the list whether or not it was ever touched: "never opened
// promotions" is a finding, and a list of only the used areas hides it.
//
// A row opens to the individual actions underneath it; the area line is
// the reading, the actions are the evidence.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { ICON, ListChecks } from '../../icons';
import DisclosureCaret from '../../components/common/DisclosureCaret';
import { useFormatters } from '../../hooks/useFormatters';
import { Panel, RelativeTime } from './primitives';
import { eventLabel } from './text';

export default function FeatureAreasPanel({ areas, windowDays }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const [open, setOpen] = useState(() => new Set());

  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <Panel
      icon={<ListChecks size={ICON.md} />}
      title={t('storeActivity.areasPanel.title')}
      subtitle={t('storeActivity.areasPanel.subtitle')}
    >
      <table className="sa-table">
        <thead>
          <tr>
            <th />
            <th className="sa-table-num">{t('storeActivity.areasPanel.window', { count: windowDays })}</th>
            {/* The all-time count is the column a phone drops: the window
                count and the last use already say whether the area is used. */}
            <th className="sa-table-num sa-col-roomy">{t('storeActivity.areasPanel.total')}</th>
            <th className="sa-table-when">{t('storeActivity.areasPanel.lastUsed')}</th>
          </tr>
        </thead>
        <tbody>
          {areas.map((area) => {
            const used = area.uses_total > 0;
            const expanded = open.has(area.key);
            return (
              <React.Fragment key={area.key}>
                <tr className={`sa-area${used ? '' : ' sa-area--unused'}`}>
                  <td>
                    {used
                      ? (
                        <Tooltip
                          title={t(expanded
                            ? 'storeActivity.areasPanel.hideEvents'
                            : 'storeActivity.areasPanel.showEvents')}
                          describeChild
                        >
                          <button
                            type="button"
                            className="sa-area-toggle"
                            onClick={() => toggle(area.key)}
                            aria-expanded={expanded}
                          >
                            <DisclosureCaret open={expanded} size={ICON.sm} />
                            {t(`storeActivity.areas.${area.key}`)}
                          </button>
                        </Tooltip>
                      )
                      : <span className="sa-area-name">{t(`storeActivity.areas.${area.key}`)}</span>}
                  </td>
                  <td className="sa-table-num">{used ? formatNumber(area.uses_window, 0) : ''}</td>
                  <td className="sa-table-num sa-col-roomy">{used ? formatNumber(area.uses_total, 0) : ''}</td>
                  <td className="sa-table-when">
                    {used
                      ? <RelativeTime value={area.last_used} />
                      : t('storeActivity.areasPanel.neverUsed')}
                  </td>
                </tr>
                {expanded && area.events.map((e) => (
                  <tr className="sa-area-event" key={e.event_type}>
                    <td>{eventLabel(e.event_type, t)}</td>
                    <td className="sa-table-num">{formatNumber(e.count_window, 0)}</td>
                    <td className="sa-table-num sa-col-roomy">{formatNumber(e.count, 0)}</td>
                    <td className="sa-table-when"><RelativeTime value={e.last_seen} /></td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}
